import { useEffect, useCallback, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}

export function Drawer({ open, onClose, title, children }: DrawerProps): ReactElement | null {
  // Animate in: mount first, then add open class on next tick
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      // Next tick so CSS transition fires after mount
      const raf = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(raf)
    } else {
      setVisible(false)
      return undefined
    }
  }, [open])

  // ESC key dismissal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, handleKeyDown])

  if (!open) return null

  return (
    <div
      className="drawer-scrim"
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-title"
      onClick={onClose}
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 200ms ease' }}
    >
      <div
        className="drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 250ms cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <div className="drawer__handle" aria-hidden="true" />
        <h2 id="drawer-title" className="drawer__title">
          {title}
        </h2>
        {children}
      </div>
    </div>
  )
}
